import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { msalInstance, loginRequest, tokenRequest, POPUP_REDIRECT_URI } from "../config/authConfig";
import { UserListApi } from "@/services/UserList.api";
import { RootState } from "./store";

// Define types for authentication state
interface AuthState {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  userRoles: string[];
  userDomain: string;
  userName: string;
  email: string;
  isClientProfileSet: boolean;
  apexId: string;
}

// Initial authentication state
const initialState: AuthState = {
  isAuthenticated: false,
  isAuthLoading: true,
  userRoles: [],
  userDomain: "",
  userName: "",
  email: "",
  isClientProfileSet: false,
  apexId: "",
};

// Define return type for login & checkAuthentication
interface AuthPayload {
  isAuthenticated: boolean;
  userRoles: string[];
  userDomain: string;
  userName: string | undefined;
  email: string | undefined;
  isClientProfileSet: boolean;
  apexId: string;
}

// Define an error type
interface AuthError {
  errorMessage: string;
}

// **Thunk: Silent Authentication Check on App Load**
export const checkAuthentication = createAsyncThunk<AuthPayload, void, { rejectValue: AuthError }>("auth/checkAuthentication", async (_, { rejectWithValue }) => {
  try {
    //  Check if logout was intentional
    if (localStorage.getItem("logoutIntent") === "true") {
      console.warn(" Skipping auto-login because logout was intentional.");
      return rejectWithValue({ errorMessage: "User intentionally logged out" });
    }

    console.log(" Initializing MSAL before authentication...");
    await msalInstance.initialize(); // Ensure MSAL is fully initialized

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      console.warn(" No active session. Triggering interactive login...");

      try {
        const response = await msalInstance.loginPopup({
          ...loginRequest,
          redirectUri: POPUP_REDIRECT_URI,
        });
        console.log(" Interactive login successful:", response);
      } catch (interactiveError) {
        console.error(" Interactive login failed:", interactiveError);
        return rejectWithValue({
          errorMessage: "Login required. User interaction needed.",
        });
      }
    }

    // Fetch the latest accounts **AFTER** attempting silent login
    const activeAccounts = msalInstance.getAllAccounts();
    if (activeAccounts.length === 0) {
      return rejectWithValue({
        errorMessage: "Login failed. No account found after login attempt.",
      });
    }

    msalInstance.setActiveAccount(activeAccounts[0]);

    // Fetch API token
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: activeAccounts[0],
    });
    const tokenPayload = JSON.parse(atob(response.accessToken.split(".")[1]));
    // const userRoles: string[] = tokenPayload.roles || [];

    // console.log(" Silent Login Successful. User Roles:", userRoles);

    var currentUser = await UserListApi.checkUserRoles("", "");
    console.log("🌐 API Response from UserListApi.checkUserRoles:", currentUser);
    console.log("🔍 Extracted Data:", {
      userRole: currentUser.userRole,
      userDomain: currentUser.domain,
      isClientProfileSet: currentUser.isClientprofileSet,
      apexId: currentUser.apexId,
      fullUserObject: currentUser
    });
    return {
      isAuthenticated: true,
      userName: activeAccounts[0].name,
      userRoles: currentUser.userRole,
      userDomain: currentUser.domain || "",
      email: tokenPayload.unique_name,
      isClientProfileSet: currentUser.isClientprofileSet || false,
      apexId: currentUser.apexId || "",
    };
  } catch (error) {
    return rejectWithValue({
      errorMessage: "Silent login failed: " + (error as Error).message,
    });
  }
});

// **Thunk: Manual Login**
export const login = createAsyncThunk<AuthPayload, void, { rejectValue: AuthError }>("auth/login", async (_, { rejectWithValue }) => {
  try {
    await msalInstance.loginPopup({
      ...loginRequest,
      redirectUri: POPUP_REDIRECT_URI,
    });
    const accounts = msalInstance.getAllAccounts();
    msalInstance.setActiveAccount(accounts[0]);

    // Fetch API token after login
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: accounts[0],
    });

    // Clear logout flag because user logged in manually
    if (localStorage.getItem("logoutIntent") === "true") {
      localStorage.removeItem("logoutIntent");
    }

    const tokenPayload = JSON.parse(atob(tokenResponse.accessToken.split(".")[1]));
    // const userRoles: string[] = tokenPayload.roles || [];

    // console.log("Login Successful. User Roles:", userRoles);
    // for current user, we don't need to add the user name and id
    var currentUser = await UserListApi.checkUserRoles("", "");
    console.log("🌐 API Response from UserListApi.checkUserRoles (Manual Login):", currentUser);
    console.log("🔍 Extracted Data (Manual Login):", {
      userRole: currentUser.userRole,
      userDomain: currentUser.domain,
      isClientprofileSet: currentUser.isClientprofileSet,
      apexId: currentUser.apexId,
      fullUserObject: currentUser
    });
    return { 
      isAuthenticated: true, 
      userName: accounts[0].name, 
      userRoles: currentUser.userRole, 
      userDomain: currentUser.domain || "",
      email: tokenPayload.unique_name,
      isClientProfileSet: currentUser.isClientprofileSet || false,
      apexId: currentUser.apexId || ""
    };
  } catch (error) {
    return rejectWithValue({
      errorMessage: "Login failed: " + (error as Error).message,
    });
  }
});

// **Thunk: Logout Function**
export const logout = createAsyncThunk<void, void, { rejectValue: AuthError }>("auth/logout", async (_, { rejectWithValue }) => {
  try {
    localStorage.setItem("logoutIntent", "true"); // Set logout flag
    await msalInstance.logoutPopup();
  } catch (error) {
    return rejectWithValue({
      errorMessage: "Logout failed: " + (error as Error).message,
    });
  }
});

// **Auth Slice**
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsClientProfileSet(state, action) {
      state.isClientProfileSet = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Silent Login (on App Load)
      .addCase(checkAuthentication.fulfilled, (state, action: PayloadAction<AuthPayload>) => {
        console.log("🔐 Silent Login - Redux State Updated:");
        console.log("📥 Action Payload:", action.payload);
        state.isAuthenticated = action.payload.isAuthenticated;
        state.isAuthLoading = false;
        state.userName = action.payload.userName || "Unknown User";
        state.userRoles = action.payload.userRoles;
        state.userDomain = action.payload.userDomain;
        state.email = action.payload.email || "";
        state.isClientProfileSet = action.payload.isClientProfileSet;
        state.apexId = action.payload.apexId || "";
        console.log("🏪 Complete Redux Auth State:", {
          isAuthenticated: state.isAuthenticated,
          userName: state.userName,
          userRoles: state.userRoles,
          email: state.email,
          isClientProfileSet: state.isClientProfileSet,
          apexId: state.apexId
        });
      })
      .addCase(checkAuthentication.rejected, (state, action) => {
        console.warn("Silent login rejected:", action.payload?.errorMessage);
        state.isAuthenticated = false;
        state.isAuthLoading = false;
        state.userRoles = [];
        state.userName = "";
        state.email = "";
        state.isClientProfileSet = false;
        state.apexId = "";
      })
      // Manual Login
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthPayload>) => {
        console.log("🔐 Manual Login - Redux State Updated:");
        console.log("📥 Action Payload:", action.payload);
        state.isAuthenticated = action.payload.isAuthenticated;
        state.userName = action.payload.userName || "Unknown User";
        state.userRoles = action.payload.userRoles;
        state.userDomain = action.payload.userDomain;
        state.email = action.payload.email || "";
        state.isClientProfileSet = action.payload.isClientProfileSet;
        state.apexId = action.payload.apexId || "";
        console.log("🏪 Complete Redux Auth State:", {
          isAuthenticated: state.isAuthenticated,
          userName: state.userName,
          userRoles: state.userRoles,
          email: state.email,
          isClientProfileSet: state.isClientProfileSet,
          apexId: state.apexId
        });
      })
      .addCase(login.rejected, (state, action) => {
        console.error("Login failed:", action.payload?.errorMessage);
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        console.log("Logout - Redux State Cleared");
        state.isAuthenticated = false;
        state.userRoles = [];
        state.userName = "";
        state.email = "";
        state.isClientProfileSet = false;
        state.apexId = "";
      });
  },
});

export const { setIsClientProfileSet } = authSlice.actions;
export default authSlice.reducer;

export const selectAuth = (state: RootState) => state.auth;
export const selectIsClientProfileSet = (state: RootState) => state.auth.isClientProfileSet;
